import React, { useState } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Mail, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  full_name: '',
  personal_email: '',
  phone: '',
  department: '',
  position: '',
  date_of_birth: '',
  desired_username: '',
  justification: '',
};

const DEPARTMENTS = [
  'Direction',
  'Coordination terrain',
  'Production agricole & élevage',
  'Formations FPT',
  'Communication',
  'Administration & Finance',
  'Suivi-évaluation',
  'Autre',
];

export default function EmailRequestPage() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/email-requests`, {
        ...form,
        desired_username: form.desired_username.toLowerCase().trim(),
      });
      setSubmitted(true);
      toast.success('Demande envoyée. Vous recevrez un e-mail de confirmation.');
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erreur lors de l'envoi de la demande";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <section className="section-padding bg-[#F9F8F6]">
          <div className="container mx-auto px-6 max-w-2xl">
            <div className="card text-center" data-testid="email-request-success">
              <CheckCircle2 className="text-[#4A6B48] mx-auto mb-4" size={56} />
              <h1 className="text-3xl font-bold text-[#2E332D] mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Demande envoyée !
              </h1>
              <p className="text-[#5C665A] leading-relaxed mb-4">
                Votre demande d&apos;adresse e-mail <strong>{form.desired_username}@cresa-asbl.org</strong> a
                été reçue. Un e-mail de confirmation a été envoyé à <strong>{form.personal_email}</strong>.
              </p>
              <p className="text-sm text-[#5C665A] italic">
                L&apos;administration CRESA examinera votre demande sous quelques jours ouvrables.
              </p>
              <button onClick={() => { setSubmitted(false); setForm(EMPTY); }}
                data-testid="new-email-request-button"
                className="mt-6 btn-secondary">
                Soumettre une nouvelle demande
              </button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-[#F9F8F6]" data-testid="email-request-page">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <Mail className="text-[#4A6B48]" size={40} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Demander une adresse e-mail CRESA
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto">
              Membres de l&apos;équipe et collaborateurs réguliers : remplissez ce formulaire pour
              obtenir une adresse <strong>@cresa-asbl.org</strong>. Votre demande sera examinée
              par l&apos;administration.
            </p>
          </div>

          <div className="card bg-[#D87D4A]/10 border-l-4 border-[#D87D4A] mb-6 flex items-start gap-3">
            <AlertCircle className="text-[#D87D4A] shrink-0 mt-0.5" size={20} />
            <p className="text-sm text-[#2E332D]">
              <strong>Information :</strong> ce formulaire est réservé aux membres de l&apos;équipe CRESA,
              partenaires actifs et collaborateurs. Les demandes non justifiées seront rejetées.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="card space-y-4" data-testid="email-request-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Nom complet *</label>
                <input type="text" required value={form.full_name}
                  onChange={(e) => update('full_name', e.target.value)}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="email-req-name-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">E-mail personnel *</label>
                <input type="email" required value={form.personal_email}
                  onChange={(e) => update('personal_email', e.target.value)}
                  placeholder="pour recevoir notre réponse"
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="email-req-personal-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Téléphone</label>
                <input type="tel" value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="email-req-phone-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Département</label>
                <select value={form.department}
                  onChange={(e) => update('department', e.target.value)}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                  data-testid="email-req-department-select">
                  <option value="">— Sélectionner —</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Fonction / Poste *</label>
              <input type="text" required value={form.position}
                onChange={(e) => update('position', e.target.value)}
                placeholder="Ex. : Coordinateur terrain, Bénévole, Stagiaire…"
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="email-req-position-input" />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Date de naissance *</label>
              <input type="date" required value={form.date_of_birth}
                onChange={(e) => update('date_of_birth', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="email-req-dob-input" />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">
                Adresse souhaitée *
              </label>
              <div className="flex items-stretch">
                <input type="text" required value={form.desired_username}
                  onChange={(e) => update('desired_username', e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
                  pattern="^[a-z0-9][a-z0-9._-]{1,30}[a-z0-9]$"
                  placeholder="prenom.nom"
                  className="flex-1 px-4 py-2 border border-[#E5E3DB] rounded-l-lg border-r-0"
                  data-testid="email-req-username-input" />
                <span className="inline-flex items-center px-3 bg-[#4A6B48]/10 border border-[#E5E3DB] border-l-0 rounded-r-lg text-sm text-[#4A6B48] font-bold">
                  @cresa-asbl.org
                </span>
              </div>
              <p className="text-xs text-[#5C665A] mt-1">
                Lettres minuscules, chiffres, points et tirets uniquement. Ex. : <code>jean.dupont</code>
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">
                Justification de la demande *
              </label>
              <textarea required rows={4} value={form.justification}
                onChange={(e) => update('justification', e.target.value)}
                placeholder="Expliquez votre rôle au sein de CRESA et l'usage prévu de cette adresse e-mail."
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="email-req-justification-input" />
            </div>

            <button type="submit" disabled={submitting}
              data-testid="email-req-submit-button"
              className="btn-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50">
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
              {submitting ? 'Envoi en cours…' : 'Envoyer ma demande'}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
